import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { helpSection } from "../../help/content/helpContent";
import { helpLanguageOf } from "../../help/content/helpLanguage";
import { CARD } from "../model/cardGeometry";
import type { GraphEdgeKind } from "../model/workflowGraph";
import { EDGE_KIND_ORDER, EDGE_KINDS } from "./edgeKinds";
import { InputTab, PortMark, PortType, StartMark } from "./NodePorts";
import { EdgeLine } from "./PortEdge";
import StepKindMark, { PersonInputMark } from "./StepKindMark";
import { kindAxesWords, type StepDefinitionKind } from "./stepKind";

/**
 * What every mark on the graph means (P-14, D14; F40, R-10, F112), said once each (F60): the
 * step kinds' icons first, the marks a reader meets first, each meaning with what the kind says
 * on the two step axes, and the raised hand of a step that needs a person's input although its
 * kind doesn't (WD-1+3), then the three edges and the
 * dotted line into a step that waits for Run, an input's required and optional outlines in
 * one entry, a port's name and data type, and the hollow output no later step uses. Each
 * mark is drawn by the canvas's own components (`StepKindMark`, `EdgeLine`, `PortMark`,
 * `InputTab`, `PortType`), so the legend shows exactly what the canvas does; a step kind's
 * word comes from the process ontology, as on its cards, and its meaning is said in the
 * Help glossary's own words (IM-RULES P-F). One list: opening it
 * shows each meaning beside its own mark, and the rule for crossings beneath (F112). Closed, a
 * narrow view keeps the kinds and the hand and leaves the lines and ports to the opened list
 * (T1-5). The read view and the editor show the same legend.
 */

/** `workflows.legend.samples.<key>` and `workflows.legend.meanings.<key>`; a step kind has a meaning only. */
type SampleKey = "passesResult" | "readsSaved" | "then" | "waitsForRun" | "inputNeed" | "startInput" | "portLabel" | "unused";

/** One entry: its mark with its name, and what it means; a secondary one waits for the opened list in a narrow view. */
interface LegendEntry {
  readonly key: string;
  readonly mark: ReactNode;
  readonly meaning: string;
  readonly secondary: boolean;
}

/** The step kinds a card shows, in the order of `StepKindMark`'s icons. */
const STEP_KINDS: readonly StepDefinitionKind[] = ["human", "agent", "tool"];

/** The strip a sample is drawn on (`app-flow-legend-sample`), and where its ends sit. */
const STRIP = { width: 64, height: 22, from: 6, to: 58 } as const;

export default function GraphLegend() {
  const { t, i18n } = useTranslation();
  const language = helpLanguageOf(i18n.resolvedLanguage);
  const [open, setOpen] = useState(false);
  const sampled = (key: SampleKey, sample: ReactNode): LegendEntry => ({
    key,
    mark: <>{sample}{t(`workflows.legend.samples.${key}`)}</>,
    meaning: t(`workflows.legend.meanings.${key}`),
    secondary: true,
  });
  const entries: readonly LegendEntry[] = [
    ...STEP_KINDS.map((kind) => ({ key: kind, mark: <StepKindMark kind={kind} />, meaning: kindMeaning(helpSection(language, "glossary", `step-${kind}`).body, kindAxesWords(kind, t)), secondary: false })),
    { key: "personInput", mark: <><PersonInputMark words={t("workflows.axes.needsPerson")} />{t("workflows.axes.needsPerson")}</>, meaning: t("workflows.legend.meanings.personInput"), secondary: false },
    ...EDGE_KIND_ORDER.map((kind) => sampled(EDGE_KINDS[kind].sample, <LineSample kind={kind} waitsForRun={false} />)),
    sampled("waitsForRun", <LineSample kind="then" waitsForRun={true} />),
    sampled("inputNeed", <><PortMark mark={{ direction: "input", need: "required" }} /><PortMark mark={{ direction: "input", need: "optional" }} /></>),
    sampled("startInput", <StartMark text={t("workflows.node.port.start")} />),
    sampled("portLabel", <PortType text={t("workflows.legend.portSample")} />),
    sampled("unused", <PortMark mark={{ direction: "output", used: false, read: false }} />),
  ];
  return (
    <div className="app-flow-legend-block">
      <ul className={open ? "app-flow-legend app-flow-legend-open" : "app-flow-legend"} aria-label={t("workflows.legend.label")}>
        {entries.map(({ key, mark, meaning, secondary }) => (
          <li key={key} title={open ? undefined : meaning} className={secondary ? "app-flow-legend-secondary" : undefined}>
            <span className="app-flow-legend-mark">{mark}</span>
            {open ? <span className="app-flow-legend-meaning">{meaning}</span> : null}
          </li>
        ))}
      </ul>
      {open ? <p className="app-flow-legend-rule">{t("workflows.legend.rule")}</p> : null}
      <button type="button" className="app-link app-flow-legend-toggle" aria-expanded={open} onClick={() => { setOpen(!open); }}>
        {t("workflows.legend.help")}
      </button>
    </div>
  );
}

/** A kind's meaning, then what the kind itself says on the two step axes (WD-1+3), so a card marks only the rest; said once, in the meaning, not beside the mark (T1-5). */
function kindMeaning(body: string, axes: readonly string[]): string {
  return axes.length === 0 ? body : `${body} ${axes.join(" · ")}.`;
}

/**
 * One edge from end to end, ending where the canvas ends it. A result runs from an
 * output's mark into an input row's circle; a read runs from an input card's output down
 * into a tab on the next card's top edge; a "then" runs from one card's edge to the next.
 */
function LineSample({ kind, waitsForRun }: { kind: GraphEdgeKind; waitsForRun: boolean }) {
  const { width, height, from, to } = STRIP;
  const middle = height / 2;
  let ends: ReactNode;
  let d: string;
  switch (kind) {
    case "step_result":
      ends = (
        <>
          <Placed x={from} y={middle}><PortMark mark={{ direction: "output", used: true, read: false }} /></Placed>
          <Placed x={to} y={middle}><PortMark mark={{ direction: "input", need: "required" }} /></Placed>
        </>
      );
      // From the output's centre to just outside the input's circle, as `cardGeometry` anchors it.
      d = `M${String(from)} ${String(middle)}H${String(to - CARD.dotRadius)}`;
      break;
    case "read":
      ends = (
        <>
          <Placed x={from} y={4}><PortMark mark={{ direction: "output", used: true, read: true }} /></Placed>
          <InputTab need="required" style={{ left: to - 9, top: height - 9, width: 18, height: 9 }} />
        </>
      );
      d = `M${String(from)} 4H${String(to - 8)}Q${String(to)} 4 ${String(to)} ${String(height - 9)}`;
      break;
    case "then":
      ends = (
        <>
          <span className="app-flow-legend-card" style={{ left: from - 5, top: middle - 9 }} />
          <span className="app-flow-legend-card" style={{ left: to - 5, top: middle - 9 }} />
        </>
      );
      d = `M${String(from + 5)} ${String(middle)}H${String(to - 5)}`;
      break;
  }
  return (
    <span className="app-flow-legend-sample" aria-hidden="true">
      {ends}
      <svg width={width} height={height} focusable="false">
        <EdgeLine d={d} kind={kind} waitsForRun={waitsForRun} />
      </svg>
    </span>
  );
}

/** A port's mark centred on (`x`, `y`) of the strip. */
function Placed({ x, y, children }: { x: number; y: number; children: ReactNode }) {
  return (
    <span className="absolute grid -translate-x-1/2 -translate-y-1/2 place-items-center" style={{ left: x, top: y }}>
      {children}
    </span>
  );
}
